import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import translations from '../translations';

const BenefitsSection = styled.section`
  padding: 100px 0;
  background-color: ${props => props.theme === 'dark' ? '#121212' : '#ffffff'};
  position: relative;
  overflow: hidden;
`;

const BenefitsContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
  position: relative;
  z-index: 2;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: ${props => props.theme === 'dark' ? '#f8f9fa' : '#333'};
  margin-bottom: 1rem;
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: ${props => props.theme === 'dark' ? '#ced4da' : '#6c757d'};
  max-width: 700px;
  margin: 0 auto;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 40px;
  position: relative;
  z-index: 2;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const BenefitItem = styled(motion.div)`
  display: flex;
  flex-direction: column;
  
  @media (min-width: 576px) {
    flex-direction: row;
  }
`;

const BenefitIconWrapper = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 12px;
  background: ${props => props.theme === 'dark' ? '#23272f' : '#fff'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  border: 1.5px solid ${props => props.theme === 'dark' ? '#23272f' : '#fff'};
  transition: background 0.3s, color 0.3s;
  
  @media (min-width: 576px) {
    margin-right: 25px;
    margin-bottom: 0;
  }
`;

const BenefitIcon = styled.div`
  font-size: 1.8rem;
  color: ${props => props.theme === 'dark' ? '#fff' : '#23272f'};
  background: ${props => props.theme === 'dark' ? '#23272f' : '#fff'};
  border-radius: 50%;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  border: 1.5px solid ${props => props.theme === 'dark' ? '#23272f' : '#fff'};
  transition: background 0.3s, color 0.3s;
`;

const BenefitContent = styled.div`
  flex: 1;
`;

const BenefitTitle = styled.h3`
  font-size: 1.25rem;
  color: ${props => props.theme === 'dark' ? '#f8f9fa' : '#333'};
  margin-bottom: 10px;
`;

const BenefitDescription = styled.p`
  font-size: 1rem;
  color: ${props => props.theme === 'dark' ? '#ced4da' : '#6c757d'};
  line-height: 1.6;
`;

const BackgroundCircle = styled.div`
  position: absolute;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(74, 108, 247, 0.1) 0%, rgba(74, 108, 247, 0) 70%);
  z-index: 1;
  
  &.top-left {
    top: -300px;
    left: -300px;
  }
  
  &.bottom-right {
    bottom: -300px;
    right: -300px;
  }
`;

const Benefits = ({ theme }) => {
  const { language } = useLanguage();
  const t = translations.benefits[language];
  
  const benefitsData = [
    {
      icon: '🔍',
      title: t.enhancedTransparency.title,
      description: t.enhancedTransparency.description
    },
    {
      icon: '⚖️',
      title: t.regulatoryCompliance.title,
      description: t.regulatoryCompliance.description
    },
    {
      icon: '🚀',
      title: t.acceleratedInnovation.title,
      description: t.acceleratedInnovation.description
    },
    {
      icon: '🛡️',
      title: t.riskMitigation.title,
      description: t.riskMitigation.description
    },
    {
      icon: '🤝',
      title: t.improvedUserAdoption.title,
      description: t.improvedUserAdoption.description
    },
    {
      icon: '📈',
      title: t.betterBusinessOutcomes.title,
      description: t.betterBusinessOutcomes.description
    }
  ];

  return (
    <BenefitsSection theme={theme} id="solutions">
      <BackgroundCircle className="top-left" />
      <BackgroundCircle className="bottom-right" />
      
      <BenefitsContainer>
        <SectionHeader>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true, amount: 0.2 }}
          >
            <SectionTitle theme={theme}>{t.title}</SectionTitle>
            <SectionSubtitle theme={theme}>
              {t.subtitle}
            </SectionSubtitle>
          </motion.div>
        </SectionHeader>
        
        <BenefitsGrid>
          {benefitsData.map((benefit, index) => (
            <BenefitItem
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <BenefitIconWrapper>
                <BenefitIcon>{benefit.icon}</BenefitIcon>
              </BenefitIconWrapper>
              <BenefitContent>
                <BenefitTitle theme={theme}>{benefit.title}</BenefitTitle>
                <BenefitDescription theme={theme}>{benefit.description}</BenefitDescription>
              </BenefitContent>
            </BenefitItem>
          ))}
        </BenefitsGrid>
      </BenefitsContainer>
    </BenefitsSection>
  );
};

export default Benefits;
